import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, Info } from 'lucide-react'
import { SEED_PROJECTS } from './data'
import { makeStarterProject } from './lib/utils'
import { ApiError, authApi, docsApi } from './lib/api'
import Home from './components/Home'
import Workspace from './components/Workspace'
import Roundtable from './components/Roundtable'
import FinishStudio from './components/FinishStudio'
import AuthView from './components/AuthView'
import HistoryModal from './components/HistoryModal'

const STORAGE_KEY = 'pageisle:v1'
const DOC_ID = 'projects'

function loadProjects() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return SEED_PROJECTS
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed) || !parsed.length) return SEED_PROJECTS
    const valid = parsed.every((project) => project && project.id && Array.isArray(project.cards))
    return valid ? parsed : SEED_PROJECTS
  } catch (error) {
    return SEED_PROJECTS
  }
}

export default function App() {
  const [projects, setProjects] = useState(loadProjects)
  const [activeProjectId, setActiveProjectId] = useState(null)
  const [view, setView] = useState('board')
  const [roundtable, setRoundtable] = useState(null)
  const [finishId, setFinishId] = useState(null)
  const [toast, setToast] = useState(null)
  const [saveState, setSaveState] = useState('saved')
  const [auth, setAuth] = useState({ status: 'loading' })
  const [bootNotice, setBootNotice] = useState('')
  const [historyOpen, setHistoryOpen] = useState(false)
  const toastTimer = useRef(null)

  const activeProject = projects.find((project) => project.id === activeProjectId) || null
  const roundtableProject = roundtable ? projects.find((project) => project.id === roundtable.projectId) || null : null
  const finishProject = finishId ? projects.find((project) => project.id === finishId) || null : null
  const hasOverlay = Boolean(roundtableProject || finishProject || historyOpen)

  const notify = useCallback((message) => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current)
    setToast({ message, id: Date.now() })
    toastTimer.current = window.setTimeout(() => setToast(null), 3400)
  }, [])

  // 启动：判断登录状态（离线/无 fetch 时进入本地模式）
  useEffect(() => {
    let cancelled = false
    async function boot() {
      if (typeof window.fetch !== 'function') {
        setAuth({ status: 'local' })
        return
      }
      try {
        const me = await authApi.me()
        if (cancelled) return
        setAuth({ status: 'ok', email: me.email })
      } catch (error) {
        if (cancelled) return
        if (error instanceof ApiError && error.status === 503) setBootNotice(error.message)
        setAuth({ status: 'anon' })
      }
    }
    boot()
    return () => { cancelled = true }
  }, [])

  // 登录后从云端拉取数据（空数据则用本地种子并等待自动同步）
  useEffect(() => {
    if (auth.status !== 'ok') return
    let cancelled = false
    docsApi.list()
      .then((data) => {
        if (cancelled) return
        const doc = (data.docs || []).find((item) => item.id === DOC_ID)
        const remote = doc && doc.payload && Array.isArray(doc.payload.projects) ? doc.payload.projects : null
        if (remote && remote.length) {
          setProjects(remote)
          try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(remote)) } catch (error) { /* ignore */ }
          notify('已从云端载入你的书架。')
        }
      })
      .catch((error) => {
        if (!cancelled) setSaveState('offline')
      })
    return () => { cancelled = true }
  }, [auth, notify])

  // 本地缓存 + 云端实时保存（防抖），每次成功保存产生一个历史版本
  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(projects)) } catch (error) { /* ignore */ }
    if (auth.status !== 'ok') {
      setSaveState(auth.status === 'local' ? 'local' : 'saved')
      return
    }
    setSaveState('saving')
    const timer = window.setTimeout(async () => {
      try {
        await docsApi.save(DOC_ID, 'projects', { projects })
        setSaveState('saved')
      } catch (error) {
        setSaveState('offline')
      }
    }, 500)
    return () => window.clearTimeout(timer)
  }, [projects, auth])

  useEffect(() => {
    const previous = document.body.style.overflow
    if (hasOverlay) document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [hasOverlay])

  useEffect(() => {
    return () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current)
    }
  }, [])

  function openProject(id, nextView = 'board') {
    setActiveProjectId(id)
    setView(nextView)
    window.scrollTo({ top: 0 })
  }

  function createProject(input) {
    const project = makeStarterProject(input)
    setProjects((current) => [project, ...current])
    setActiveProjectId(project.id)
    setView('board')
    notify('《' + project.title + '》已经放上书架。')
  }

  function updateActiveProject(updater) {
    if (!activeProjectId) return
    setProjects((current) => current.map((project) => {
      if (project.id !== activeProjectId) return project
      const next = typeof updater === 'function' ? updater(project) : { ...project, ...updater }
      return { ...next, updatedAt: next.updatedAt || new Date().toISOString() }
    }))
  }

  function openRoundtableFor(projectId, cardIds = []) {
    setFinishId(null)
    setRoundtable({ projectId, cardIds, openedAt: Date.now() })
  }

  const closeRoundtable = useCallback(() => {
    setRoundtable(null)
  }, [])

  const openRoundtableFromWorkspace = useCallback((cardIds) => {
    setFinishId(null)
    setRoundtable({ projectId: activeProjectId, cardIds, openedAt: Date.now() })
  }, [activeProjectId])

  function openFinish(projectId) {
    setRoundtable(null)
    setFinishId(projectId)
  }

  function applyFinish(output) {
    if (!finishId) return
    setProjects((current) => current.map((project) => project.id === finishId ? {
      ...project,
      cards: output.cards,
      connections: output.connections,
      updatedAt: new Date().toISOString(),
    } : project))
  }

  function saveRoundtableNote(cardId, note) {
    if (!roundtable) return
    setProjects((current) => current.map((project) => {
      if (project.id !== roundtable.projectId) return project
      return {
        ...project,
        updatedAt: new Date().toISOString(),
        cards: project.cards.map((card) => {
          if (card.id !== cardId) return card
          const previous = card.notes ? card.notes + '\n\n' : ''
          return { ...card, notes: previous + note, updatedAt: new Date().toISOString() }
        }),
      }
    }))
    notify('建议已存为卡片批注。')
  }

  const handleAuthed = useCallback((email) => {
    setAuth({ status: 'ok', email })
    notify('欢迎回到页屿。')
  }, [notify])

  const handleLogout = useCallback(async () => {
    try { await authApi.logout() } catch (error) { /* ignore */ }
    setActiveProjectId(null)
    setHistoryOpen(false)
    setAuth({ status: 'anon' })
    notify('已退出登录。')
  }, [notify])

  const handleOpenHistory = useCallback(() => {
    if (auth.status !== 'ok') {
      notify('登录后才可以查看云端版本历史。')
      return
    }
    setHistoryOpen(true)
  }, [auth.status, notify])

  const handleRestored = useCallback((restoredProjects) => {
    setProjects(restoredProjects)
    setActiveProjectId(null)
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(restoredProjects)) } catch (error) { /* ignore */ }
  }, [])

  if (auth.status === 'loading') {
    return (
      <div className="app-loading">
        <span>正在打开页屿…</span>
      </div>
    )
  }

  if (auth.status === 'anon') {
    return (
      <>
        <AuthView onAuthed={handleAuthed} notice={bootNotice} />
        {toast ? (
          <div className="toast" key={toast.id} role="status">
            <Info size={15} />
            <span>{toast.message}</span>
          </div>
        ) : null}
      </>
    )
  }

  return (
    <>
      {activeProject ? (
        <Workspace
          key={activeProject.id}
          project={activeProject}
          view={view}
          setView={setView}
          saveState={saveState}
          onBack={() => setActiveProjectId(null)}
          onUpdateProject={updateActiveProject}
          onOpenRoundtable={openRoundtableFromWorkspace}
          onOpenFinish={() => openFinish(activeProject.id)}
          onOpenHistory={handleOpenHistory}
          notify={notify}
        />
      ) : (
        <Home
          projects={projects}
          onOpenProject={openProject}
          onCreateProject={createProject}
          onOpenRoundtable={(projectId) => openRoundtableFor(projectId, [])}
          notify={notify}
          userEmail={auth.status === 'ok' ? auth.email : null}
          saveState={saveState}
          onLogout={handleLogout}
          onOpenHistory={handleOpenHistory}
        />
      )}

      {historyOpen ? (
        <HistoryModal
          docId={DOC_ID}
          onClose={() => setHistoryOpen(false)}
          onRestored={handleRestored}
          notify={notify}
        />
      ) : null}

      {roundtableProject ? (
        <Roundtable
          key={roundtable.openedAt}
          project={roundtableProject}
          initialCardIds={roundtable.cardIds}
          onClose={closeRoundtable}
          onSaveNote={saveRoundtableNote}
          onOpenFinish={() => openFinish(roundtableProject.id)}
          notify={notify}
        />
      ) : null}

      {finishProject ? (
        <FinishStudio
          key={'finish-' + finishProject.id}
          project={finishProject}
          onClose={() => setFinishId(null)}
          onApply={applyFinish}
          notify={notify}
          onOpenRoundtable={(ids) => {
            const projectId = finishProject.id
            setFinishId(null)
            openRoundtableFor(projectId, ids)
          }}
        />
      ) : null}

      {toast ? (
        <div className="toast" key={toast.id} role="status">
          {toast.message.includes('失败') ? <Info size={15} /> : <Check size={15} />}
          <span>{toast.message}</span>
        </div>
      ) : null}
    </>
  )
}
